import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { isEmpty, pick } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import type { AddressDetails } from '@swf/components/AddressResults/AddressResults';
import AddressResults from '@swf/components/AddressResults/AddressResults';
import { Country } from '@swf/core/enums';
import { select } from '@swf/core/store';

import useRequest from '../../../../../../hooks/useRequest';
import { getSpainCommunity } from '../../../../../../utils/country-states/country-states';
import Modal from '../../../../../Modal/Modal';
import SearchInput from '../../../../../SearchInput/SearchInput';

import {
  TXTWCL_Search_address,
  TXTWCL_Start_typing_your_address,
  TXTWCL_Your_address
} from './AddressSearch.messages.json';
import { container, searchInput, searchInputField } from './AddressSearch.scss';
import InvalidAddress from './components/InvalidAddress/InvalidAddress';

const addressLang = 'ENG';

const fieldNames: { [key: string]: string } = {
  streetAddress: 'address',
  city: 'city',
  state: 'state',
  zip: 'zip',
  streetName: 'street',
  buildingNumber: 'buildingNumber'
};

interface AddressNewValues {
  address: string;
  city: string;
  state: string;
  zip?: string;
  street: string;
  buildingNumber: string;
}

interface Props {
  showManualForm: () => void;
  panelVisible: boolean;
  onClose: () => void;
  selectedCountry?: string | null;
}

const AddressSearch: React.FC<Props> = ({
  showManualForm,
  panelVisible,
  onClose,
  selectedCountry = null
}) => {
  const [promptVisible, setPromptVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newValues, setNewValues] = useState<AddressNewValues>();
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);
  const site = useSelector(select.cms.getSiteId);
  const request = useRequest();
  const form = useForm();

  const {
    input: { value: country }
  } = useField('country', {
    subscription: { value: true }
  });
  const onDismiss = useCallback(() => {
    showManualForm();
  }, [showManualForm]);

  const dismissAndReset = () => {
    setSearchQuery('');
    onClose();
  };

  const setAddressDetails = useCallback(
    (details: AddressDetails) => {
      const newDetails = pick(details, [
        'address',
        'city',
        'zip',
        'state',
        'street',
        'buildingNumber'
      ]);
      const stateAsNumber = /\d/.test(newDetails.state);
      if (stateAsNumber) {
        newDetails.state = newDetails.state.replace(/\D/g, '');
      }
      if (newDetails.city) {
        newDetails.city = newDetails.city.replace(/\./g, '');
      }
      switch (true) {
        case site === Sites.CA_ON:
          setNewValues({ ...newDetails, state: 'ON' });
          break;
        case site === Sites.SPA && country === Country.Spain:
          setNewValues({ ...newDetails, state: getSpainCommunity(newDetails.state) });
          break;
        default:
          setNewValues(newDetails);
          break;
      }
    },
    [country, site]
  );

  const onResponse = useCallback(
    (body: AddressDetails[]) => {
      const [item]: AddressDetails[] = body.filter(
        (result: { language: string }) => result.language === addressLang
      );
      const addressData = item || body[0];
      const validatedData = pick(addressData, [
        'address',
        'street',
        'buildingNumber',
        'city',
        'zip'
      ]);
      const isAllDataEmpty =
        Object.keys(validatedData).length === 0 ||
        Object.values(validatedData).every((value) => isEmpty(value));
      if (isAllDataEmpty) {
        setPromptVisible(true);
      } else {
        setAddressDetails(addressData);
        onDismiss();
      }
    },
    [onDismiss, setAddressDetails]
  );

  useEffect(() => {
    const registeredFields = form.getRegisteredFields();
    if (!isEmpty(newValues)) {
      form.batch(() =>
        registeredFields
          .filter((field) => fieldNames[field])
          .forEach((field) =>
            form.change(field, newValues[fieldNames[field] as keyof AddressNewValues])
          )
      );
    }
  }, [request, searchQuery, country, newValues, form]);

  return (
    <>
      {promptVisible && (
        <InvalidAddress onClose={() => setPromptVisible(false)} onConfirm={onDismiss} />
      )}
      <Modal visible={panelVisible} title={TXTWCL_Your_address} onClose={dismissAndReset}>
        <div className={container}>
          <Translation tagName="h2" {...TXTWCL_Search_address} />
          <div className={searchInput}>
            <SearchInput
              id="address-search"
              label={TXTWCL_Start_typing_your_address}
              className={searchInputField}
              onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(value)
              }
              autoFocus={isAutofocusEnabled}
            />
          </div>
          <AddressResults
            searchQuery={searchQuery}
            onResponse={onResponse}
            onDismiss={onDismiss}
            selectedCountry={selectedCountry}
          />
        </div>
      </Modal>
    </>
  );
};

export default AddressSearch;
